import React, { useState, useEffect } from 'react';
import { IArticle } from '../../types/Article';
import styles from './ArticleForm.module.css';
import { useNavigate } from 'react-router-dom';
import { uploadImage } from '../../services/UploadService';
import { IArticleImage } from '../../types/ArticleImage';
import { 
  StarIcon,
  EyeIcon,
  EyeSlashIcon,
  PhotoIcon,
  TrashIcon,
  PlusIcon,
  InformationCircleIcon,
  DocumentTextIcon,
  TagIcon
} from '@heroicons/react/24/outline';

interface ArticleFormProps {
    initialData?: IArticle;
    onSubmit: (data: IArticle, imagesToDelete: string[]) => void;
    isEditing: boolean;
    sessionUploadsRef?: React.MutableRefObject<string[]>;
}

const emptyArticle: IArticle = { 
    id: Date.now(), 
    title: '', 
    summary: '', 
    content: '', 
    author: 'Usuario Admin', 
    category: 'Articulo', 
    imageUrl: '', 
    gallery: [], 
    publishedAt: new Date().toISOString().split('T')[0], 
    isFeatured: false, 
    isHidden: false 
};

const categories = ['Noticia', 'Tendencia', 'Estudio', 'Proyecto', 'Evento', 'Otro', 'Articulo'];

const ArticleForm: React.FC<ArticleFormProps> = ({ initialData, onSubmit, isEditing, sessionUploadsRef }) => {
    const [formData, setFormData] = useState<IArticle>(emptyArticle);
    const [isUploading, setIsUploading] = useState(false);
    const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);
    const [activeTab, setActiveTab] = useState<'content' | 'gallery'>('content');
    const navigate = useNavigate();

    useEffect(() => {
        if (initialData) {
            const sanitizedData = { 
                ...emptyArticle, 
                ...initialData, 
                gallery: initialData.gallery || [], 
                publishedAt: initialData.publishedAt ? new Date(initialData.publishedAt).toISOString().split('T')[0] : '' 
            };
            setFormData(sanitizedData);
        } else {
            setFormData(emptyArticle);
        }
    }, [initialData]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleGalleryFilesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || !formData.id) return;
        setIsUploading(true);
        for (const file of Array.from(files)) {
            try {
                const response = await uploadImage(file, 'articles', formData.id);
                const newImage: IArticleImage = {
                    id: 0,
                    url: response.url,
                    caption: '',
                    description: ''
                };
                setFormData(prev => ({ ...prev, gallery: [...prev.gallery, newImage] }));
                sessionUploadsRef?.current.push(response.url);
            } catch (error) { 
                alert(`Falló la subida de ${file.name}.`); 
            }
        }
        setIsUploading(false);
    };

    const handleRemoveFromGallery = (urlToRemove: string) => {
        setImagesToDelete(prev => [...prev, urlToRemove]);
        setFormData(prev => ({ ...prev, gallery: prev.gallery.filter(image => image.url !== urlToRemove) }));
        
        if (formData.imageUrl === urlToRemove) {
            setFormData(prev => ({ ...prev, imageUrl: '' }));
        }
    };

    const handleGalleryInputChange = (
        index: number, 
        field: 'caption' | 'description', 
        value: string
    ) => {
        setFormData(prev => {
            const newGallery = prev.gallery.map((image, i) => 
                i === index ? { ...image, [field]: value } : image
            );
            return { ...prev, gallery: newGallery };
        });
    };

    const handleSetMainImage = (url: string) => {
        setFormData(prev => ({ ...prev, imageUrl: url }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData, imagesToDelete);
    };

    return (
        <div className={styles.formContainer}>
            {/* Header con pestañas */}
            <div className={styles.formHeader}>
                <h2 className={styles.formTitle}>
                    {isEditing ? 'Editar Artículo' : 'Crear Nuevo Artículo'}
                </h2>
                <div className={styles.tabNavigation}>
                    <button
                        type="button"
                        className={`${styles.tabButton} ${activeTab === 'content' ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab('content')}
                    >
                        <DocumentTextIcon className={styles.tabIcon} />
                        Contenido
                    </button>
                    {isEditing && (
                        <button
                            type="button"
                            className={`${styles.tabButton} ${activeTab === 'gallery' ? styles.tabActive : ''}`}
                            onClick={() => setActiveTab('gallery')}
                        >
                            <PhotoIcon className={styles.tabIcon} />
                            Galería ({formData.gallery.length})
                        </button>
                    )}
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Pestaña: Contenido */}
                {activeTab === 'content' && (
                    <div className={styles.tabContent}>
                        <div className={styles.formGrid}>
                            {/* Columna 1 */}
                            <div className={styles.formColumn}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="title" className={styles.formLabel}>
                                        Título del Artículo *
                                    </label>
                                    <input 
                                        type="text" 
                                        id="title" 
                                        name="title" 
                                        value={formData.title} 
                                        onChange={handleChange} 
                                        className={styles.formInput}
                                        placeholder="Ej: Las últimas tendencias en arquitectura sostenible"
                                        required 
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="category" className={styles.formLabel}>
                                        Categoría *
                                    </label>
                                    <select 
                                        id="category" 
                                        name="category" 
                                        value={formData.category} 
                                        onChange={handleChange} 
                                        className={styles.formSelect}
                                        required
                                    >
                                        {categories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="author" className={styles.formLabel}>
                                        Autor
                                    </label>
                                    <input 
                                        type="text" 
                                        id="author" 
                                        name="author" 
                                        value={formData.author} 
                                        onChange={handleChange} 
                                        className={styles.formInput}
                                        placeholder="Ej: María González"
                                    />
                                </div>
                            </div>

                            {/* Columna 2 */}
                            <div className={styles.formColumn}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="publishedAt" className={styles.formLabel}>
                                        Fecha de Publicación
                                    </label>
                                    <input 
                                        type="date" 
                                        id="publishedAt" 
                                        name="publishedAt" 
                                        value={formData.publishedAt} 
                                        onChange={handleChange} 
                                        className={styles.formInput}
                                    />
                                </div>

                                <div className={styles.optionsGroup}>
                                    <label className={styles.formLabel}>Opciones del Artículo</label>
                                    <div className={styles.checkboxCard}>
                                        <label className={styles.checkboxLabel}>
                                            <input 
                                                type="checkbox" 
                                                id="isFeatured" 
                                                name="isFeatured" 
                                                checked={formData.isFeatured} 
                                                onChange={handleChange} 
                                                className={styles.checkboxInput}
                                            />
                                            <span className={styles.checkboxCustom}></span>
                                            <StarIcon className={styles.checkboxIcon} />
                                            <span className={styles.checkboxText}>
                                                <strong>Artículo Destacado</strong>
                                                <small>Aparecerá en la página principal</small>
                                            </span>
                                        </label>
                                        
                                        <label className={styles.checkboxLabel}>
                                            <input 
                                                type="checkbox" 
                                                id="isHidden" 
                                                name="isHidden" 
                                                checked={formData.isHidden} 
                                                onChange={handleChange} 
                                                className={styles.checkboxInput}
                                            />
                                            <span className={styles.checkboxCustom}></span>
                                            {formData.isHidden ? (
                                                <EyeSlashIcon className={styles.checkboxIcon} />
                                            ) : (
                                                <EyeIcon className={styles.checkboxIcon} />
                                            )}
                                            <span className={styles.checkboxText}>
                                                <strong>Artículo Oculto</strong>
                                                <small>No visible al público</small>
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Resumen */}
                        <div className={styles.formGroupFull}>
                            <label htmlFor="summary" className={styles.formLabel}>
                                Resumen *
                                <span className={styles.charCount}>
                                    {formData.summary.length}/500 caracteres
                                </span>
                            </label>
                            <textarea 
                                id="summary" 
                                name="summary" 
                                value={formData.summary} 
                                onChange={handleChange} 
                                className={styles.formTextarea}
                                placeholder="Escribe un resumen atractivo que aparecerá en la vista previa del artículo..."
                                rows={4}
                                maxLength={500}
                                required 
                            />
                            <div className={styles.textareaFooter}>
                                <span className={styles.helperText}>
                                    Este resumen aparecerá en las tarjetas de vista previa del artículo
                                </span>
                            </div>
                        </div>

                        {/* Contenido */}
                        <div className={styles.formGroupFull}>
                            <label htmlFor="content" className={styles.formLabel}>
                                Contenido del Artículo *
                                <span className={styles.charCount}>
                                    {formData.content.length}/10000 caracteres
                                </span>
                            </label>
                            <textarea 
                                id="content" 
                                name="content" 
                                value={formData.content} 
                                onChange={handleChange} 
                                className={styles.formTextarea}
                                placeholder="Desarrolla aquí el contenido completo de tu artículo. Puedes incluir introducción, desarrollo, conclusiones..."
                                rows={12}
                                maxLength={10000}
                                required 
                            />
                            <div className={styles.textareaFooter}>
                                <span className={styles.helperText}>
                                    Este contenido aparecerá en la página completa del artículo
                                </span>
                            </div>
                        </div>

                        {!isEditing && (
                            <div className={styles.creationMessage}>
                                <InformationCircleIcon className={styles.messageIcon} />
                                <div>
                                    <strong>Guarda el artículo primero</strong>
                                    <p>Para agregar imágenes a la galería, primero guarda el artículo y luego edítalo desde la pestaña Galería.</p>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Pestaña: Galería */}
                {activeTab === 'gallery' && (
                    <div className={styles.tabContent}>
                        <div className={styles.gallerySection}>
                            <div className={styles.sectionHeader}>
                                <PhotoIcon className={styles.sectionIcon} />
                                <div>
                                    <h3 className={styles.sectionTitle}>Galería de Imágenes</h3>
                                    <p className={styles.sectionDescription}>
                                        Gestiona las imágenes de tu artículo. Establece una imagen principal y añade descripciones detalladas.
                                    </p>
                                </div>
                            </div>

                            <div className={styles.uploadSection}>
                                <label htmlFor="gallery" className={styles.uploadLabel}>
                                    <PlusIcon className={styles.uploadIcon} />
                                    <span>Agregar Imágenes</span>
                                    <input 
                                        type="file" 
                                        id="gallery" 
                                        name="gallery" 
                                        onChange={handleGalleryFilesChange} 
                                        multiple 
                                        disabled={isUploading}
                                        accept="image/*"
                                        className={styles.uploadInput}
                                    />
                                </label>
                                {isUploading && (
                                    <div className={styles.uploadingIndicator}>
                                        <div className={styles.spinner}></div>
                                        Subiendo imágenes...
                                    </div>
                                )}
                            </div>

                            {formData.gallery.length > 0 ? (
                                <div className={styles.galleryGrid}>
                                    {formData.gallery.map((image, index) => {
                                        const isMainImage = formData.imageUrl === image.url;
                                        return (
                                            <div key={image.id || image.url} className={`${styles.galleryCard} ${isMainImage ? styles.isMain : ''}`}>
                                                <div className={styles.galleryImageContainer}>
                                                    <img src={image.url} alt={`Imagen ${index + 1}`} className={styles.galleryImage} />
                                                    
                                                    <div className={styles.imageOverlay}>
                                                        <button 
                                                            type="button" 
                                                            className={styles.imageActionButton}
                                                            onClick={() => window.open(image.url, '_blank')}
                                                            title="Ver imagen completa"
                                                        >
                                                            <EyeIcon className={styles.actionIcon} />
                                                        </button>
                                                        
                                                        {!isMainImage && (
                                                            <button 
                                                                type="button" 
                                                                className={styles.imageActionButton}
                                                                onClick={() => handleSetMainImage(image.url)}
                                                                title="Establecer como imagen principal"
                                                            >
                                                                <StarIcon className={styles.actionIcon} />
                                                            </button>
                                                        )}
                                                        
                                                        <button 
                                                            type="button" 
                                                            className={`${styles.imageActionButton} ${styles.deleteButton}`}
                                                            onClick={() => handleRemoveFromGallery(image.url)}
                                                            title="Eliminar imagen"
                                                        >
                                                            <TrashIcon className={styles.actionIcon} />
                                                        </button>
                                                    </div>

                                                    {isMainImage && (
                                                        <div className={styles.mainImageBadge}>
                                                            <StarIcon className={styles.badgeIcon} />
                                                            Principal
                                                        </div>
                                                    )}
                                                </div>

                                                <div className={styles.imageInfo}>
                                                    <div className={styles.inputGroup}>
                                                        <label className={styles.inputLabel}>
                                                            Título de la imagen
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={image.caption || ''}
                                                            onChange={(e) => handleGalleryInputChange(index, 'caption', e.target.value)}
                                                            className={styles.infoInput}
                                                            placeholder="Ej: Gráfica de tendencias, Diagrama conceptual..."
                                                        />
                                                    </div>

                                                    <div className={styles.inputGroup}>
                                                        <label className={styles.inputLabel}>
                                                            Descripción detallada
                                                            <span className={styles.charCount}>
                                                                {image.description?.length || 0}/300 caracteres
                                                            </span>
                                                        </label>
                                                        <textarea
                                                            value={image.description || ''}
                                                            onChange={(e) => handleGalleryInputChange(index, 'description', e.target.value)}
                                                            className={styles.infoTextarea}
                                                            placeholder="Describe esta imagen en detalle: contexto, elementos importantes, relación con el contenido..."
                                                            rows={3}
                                                            maxLength={300}
                                                        />
                                                        <div className={styles.helperText}>
                                                            Esta descripción aparecerá junto a la imagen en el artículo
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className={styles.emptyGallery}>
                                    <PhotoIcon className={styles.emptyIcon} />
                                    <h4>No hay imágenes en la galería</h4>
                                    <p>Agrega imágenes para enriquecer tu artículo</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Botones de acción */}
                <div className={styles.formActions}>
                    <button 
                        type="button" 
                        className={styles.cancelButton}
                        onClick={() => navigate('/articles')}
                    >
                        Cancelar
                    </button>
                    
                    <div className={styles.actionButtons}>
                        {activeTab === 'gallery' && (
                            <button 
                                type="button" 
                                className={styles.secondaryButton}
                                onClick={() => setActiveTab('content')}
                            >
                                ← Volver al Contenido
                            </button>
                        )}
                        
                        <button 
                            type="submit" 
                            className={styles.primaryButton}
                            disabled={isUploading}
                        >
                            {isUploading ? (
                                <>
                                    <div className={styles.spinner}></div>
                                    Procesando...
                                </>
                            ) : (
                                isEditing ? 'Guardar Cambios' : 'Crear Artículo'
                            )}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ArticleForm;